import React, { forwardRef, ReactElement, Ref, useCallback, useState, useEffect } from 'react';
import CustomDialog from '../../common/Dialog';
import { useStyles } from '../styles/BasePartialRejection.styles';
import { TransitionProps } from '@material-ui/core/transitions';
import Slide from '@material-ui/core/Slide';
import { Button, Grid, Typography } from '@material-ui/core';
import SelectBox from '../../common/SelectBox';
import BundleProducts from '../../BundleProducts/index';
import { decodeHtmlEntities } from 'utils/textUtils';
import VirtualProductBadge from 'SharedComponent/VirtualProductBadge';
import { getVirtualProductDisplayInfo } from 'utils/business/virtualproduct/virtualProductUtils';
import { isSubstitutedProducts } from 'utils/productUtils';

interface BasePartialRejectionDialogProps {
  open: boolean;
  consignment: any;
  items: any;
  bundleProduct?: any;
  closePopup: () => void;
  handleDialogAction?: (params: any) => void;
  rejectionReasonList: any[];
  revisedAmountAfterCalculation?: string;
  fetchRejectionReasons: (countryCode: any) => void;
  rejectionDataTransform: (items: any) => any;
  bundleRejectionDataTransform: (bundleProduct: any) => any;
  countryCode: any;
  metadataSelector: (consignment: any) => any;
}

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children?: ReactElement<any, any> },
  ref: Ref<unknown>
) {
  return <Slide direction="left" ref={ref} {...props} />;
});

const BasePartialRejectionDialog = (props: BasePartialRejectionDialogProps) => {
  const classes = useStyles();
  const {
    open,
    consignment,
    items,
    bundleProduct,
    closePopup,
    handleDialogAction,
    rejectionReasonList,
    revisedAmountAfterCalculation,
    fetchRejectionReasons,
    rejectionDataTransform,
    bundleRejectionDataTransform,
    countryCode,
    metadataSelector,
  } = props;
  const generalReason = '';
  const [rejections, setRejections] = useState([] as any);
  const [products, setProducts] = useState([] as any);
  const [bundleProducts, setBundleProducts] = useState([] as any);
  const metadata: any = metadataSelector(consignment);

  const dialogPaperProps = {
    classes: {
      root: classes.dialogPaperPropsRoot,
    },
    square: true,
  };

  const handleDialogClose = useCallback(() => {
    closePopup();
  }, [closePopup]);

  const validateForm = (products: any, bundleItems: any) => {
    const newProducts = [...products];
    const newBundleItems = [...bundleItems];
    let rejectionReason = false;
    newProducts.forEach((element: any) => {
      if (
        (element?.rejectedQty !== '0' &&
          element?.showReason === true &&
          element?.itemDeliveryReason &&
          element?.itemDeliveryReason?.length === 0) ||
        (element?.rejectedQty !== '0' &&
          element?.itemDeliveryReason &&
          element?.itemDeliveryReason?.length > 0 &&
          element?.itemDeliveryReason[0]?.name === '')
      ) {
        element.error = 'Please select reason';
        rejectionReason = true;
      }
    });
    newBundleItems?.forEach((element: any) => {
      if (
        (element?.rejectedQty !== '0' &&
          element?.showReason === true &&
          element?.itemDeliveryReason &&
          element?.itemDeliveryReason?.length === 0) ||
        (element?.rejectedQty !== '0' &&
          element?.itemDeliveryReason &&
          element?.itemDeliveryReason?.length > 0 &&
          element?.itemDeliveryReason[0]?.name === '')
      ) {
        element.error = 'Please select reason';
        rejectionReason = true;
      }
    });
    setBundleProducts([...newBundleItems]);
    setProducts([...newProducts]);
    return rejectionReason;
  };

  const dialogActionHandler = useCallback(
    (type: string, values?: any, bundleItems?: any, consignment?: any) => {
      if (type === 'save') {
        if (!validateForm(values, bundleItems)) {
          handleDialogAction &&
            handleDialogAction({
              type: type,
              values: values,
              bundleItems: bundleItems,
              consignment: consignment,
            });
        }
      } else {
        closePopup();
      }
    },
    [closePopup, handleDialogAction]
  );

  const handleCommonReasonChange = (event: any) => {
    const commonReason: any = [];
    rejectionReasonList.forEach((reason: any) => {
      const productsCopy: any = [...products];
      const bundleProductCopy: any = [...bundleProducts];
      if (reason.value === event) {
        commonReason.push(reason);
        productsCopy?.forEach((prod: any) => {
          if (prod.showReason) {
            prod.itemDeliveryReason = commonReason;
          }
        });
        bundleProductCopy?.forEach((prod: any) => {
          if (prod.showReason) {
            prod.itemDeliveryReason = commonReason;
          }
        });
      }
      setProducts([...productsCopy]);
      setBundleProducts([...bundleProductCopy]);
    });
    setRejections(commonReason);
  };

  const handleItemReasonChange = (event: any, fieldName: any) => {
    const itemReason: any = [];
    rejectionReasonList.forEach((reason: any) => {
      const productsCopyNew: any = [...products];
      if (reason.value === event) {
        itemReason.push(reason);
        productsCopyNew.forEach((prod: any) => {
          if (prod.entryNumber === fieldName) {
            prod.itemDeliveryReason = itemReason;
            prod.error = '';
          }
        });
      }
      setProducts([...productsCopyNew]);
    });
    setRejections(itemReason);
  };

  const handleBundleReasonChange = (event: any, fieldName: any) => {
    const itemReason: any = [];
    rejectionReasonList.forEach((reason: any) => {
      const bundleProductCopy: any = [...bundleProducts];
      if (reason.value === event) {
        itemReason.push(reason);
        bundleProductCopy.forEach((prod: any) => {
          if (prod.id === fieldName) {
            prod.itemDeliveryReason = itemReason;
            prod.error = '';
          }
        });
      }
      setBundleProducts([...bundleProductCopy]);
    });
  };

  const handleUpdatedRejectedQty = (
    event: any,
    fieldName: any,
    isBundleProduct?: boolean
  ) => {
    let updateProducts: any;
    if (isBundleProduct) {
      updateProducts = [...bundleProducts];
    } else {
      updateProducts = [...products];
    }
    updateProducts.forEach((prod: any) => {
      const matchField = isBundleProduct ? prod.id === fieldName : prod.entryNumber === fieldName;
      if (matchField) {
        prod.rejectedQty = event;
        if (event === '0') {
          prod.showReason = false;
          prod.itemDeliveryReason.pop();
        } else {
          prod.showReason = true;
        }
      }
      if (isBundleProduct) {
        setBundleProducts([...updateProducts]);
      } else {
        setProducts([...updateProducts]);
      }
    });
  };

  const checkIfDisabled = () => {
    let counter = 0;
    let isDisabled = false;
    if (products && products.length) {
      products.forEach((item: any) => {
        if (item.rejectedQty === 0) {
          counter = counter + 1;
        }
      });
    }
    if (bundleProducts && bundleProducts.length) {
      bundleProducts.forEach((item: any) => {
        if (item.rejectedQty === 0) {
          counter = counter + 1;
        }
      });
    }
    isDisabled = counter === products?.length + bundleProducts?.length;
    return isDisabled;
  };

  const getBundleProduct = () => {
    return bundleProducts.map((item: any, index: number) => (
      <BundleProducts
        bundle={item}
        key={index}
        editMode={true}
        rejectionReasonList={rejectionReasonList}
        handleUpdatedRejectedQty={handleUpdatedRejectedQty}
        bundleCurrency={metadata?.currency}
        handleBundleReasonChange={handleBundleReasonChange}
      />
    ));
  };

  const getItems = () => {
    return products.map((item: any, index: number) => {
      const virtualProductInfo = getVirtualProductDisplayInfo(item);
      return (
      <Grid key={`${item.name.en} - ${index}`} className="item" item>
        <Grid className="itemContainer" container>
          <Grid className="itemColumn item1" item xs={4}>
            <Typography className="itemsContent">
              {item.substitutedProducts &&
              item.substitutedProducts.length &&
              item.substitutedProducts[0].name &&
              item.substitutedProducts[0].name.en
                ? decodeHtmlEntities(
                    item.substitutedProducts[0].name.en.toLowerCase()
                  )
                : item.name && item.name.en
                ? decodeHtmlEntities(item.name.en.toLowerCase())
                : ''}
            </Typography>
          {!isSubstitutedProducts(item) && <Grid item container xs="auto">
            <VirtualProductBadge
                virtualProductLabel={virtualProductInfo.virtualProductLabel}
                hasBadge={virtualProductInfo.hasBadge}
                badge={virtualProductInfo.badge}
              />
            </Grid>} 
          </Grid>
          <Grid className="itemColumn item2" item xs={2}>
            <Typography className="itemsContent">
              <SelectBox
                className="select"
                value={item.selectedRejectedQty[0].value}
                id="rejectedQty"
                inputProps={{
                  name: 'rejectedQty',
                  id: 'rejectedQty',
                }}
                items={item.rejectedQtyList || []}
                handleChange={(e: any) => handleUpdatedRejectedQty(e, item.entryNumber)}
              ></SelectBox>
            </Typography>
          </Grid>
          <Grid className="itemColumn item2" item xs={2}>
            <Typography className="itemsContent">
              <span className="green">{` ${item.shippedQty || 0} ${item.uom}`}</span>
            </Typography>
          </Grid>
          <Grid className="itemColumn item2" item xs={2}>
            <Typography className="itemsContent">{`${item.totalQty || ''}`}</Typography>
          </Grid>
          <Grid className="itemColumn item3" item xs={2}>
            <Typography className="itemsContent">{`${item.unitPrice ? item.unitPrice.toString() : ''} ${metadata && metadata.currency}`}</Typography>
          </Grid>
          {item.showReason ? (
            <Grid item xs={12}>
              <Grid container>
                <Grid item style={{ display: 'flex', alignItems: 'center' }}>
                  <Typography style={{ fontSize: '10px', color: '#2D2D2D', fontWeight: 600 }}>
                    Rejection Reason :{' '}
                  </Typography>
                </Grid>
                <Grid item style={{ paddingLeft: '8px' }}>
                  <SelectBox
                    variant="standard"
                    disableUnderline
                    value={item.itemDeliveryReason && item.itemDeliveryReason[0] && item.itemDeliveryReason[0].value}
                    id="reason"
                    inputProps={{ name: 'reason', id: 'reason' }}
                    menuPaperProps={classes.menuPropsPaper}
                    menuPropsList={classes.menuPropsList}
                    classes={{ root: classes.selectRoot, icon: classes.icon }}
                    items={rejectionReasonList || []}
                    handleChange={(e: any) => handleItemReasonChange(e, item.entryNumber)}
                  ></SelectBox>
                </Grid>
              </Grid>
            </Grid>
          ) : null}
          {item.error ? (
            <Grid>
              <Typography style={{ color: 'red', fontSize: '10px' }}>Please select reason</Typography>
            </Grid>
          ) : null}
        </Grid>
      </Grid>
    )});
  };

  const getTitleContent = () => (
    <Grid className={classes.titleContainer} container>
      <Grid item>
        <Grid className="headingContainer" container>
          <Grid item>
            <Grid container direction="column">
              <Grid item>
                <Typography className="titleHeading">
                  <span className="label">Update Rejected Items</span>
                </Typography>
              </Grid>
              <Grid item>
                <Grid container>
                  <Grid item style={{ display: 'flex', alignItems: 'center' }}>
                    <Typography style={{ fontSize: '12px', color: '#0E5AA7', fontWeight: 600 }}>
                      Common Rejection Reason :{' '}
                    </Typography>
                  </Grid>
                  <Grid item style={{ paddingLeft: '8px' }}>
                    <SelectBox
                      fullWidth
                      showSelectNone={false}
                      variant="standard"
                      value={generalReason}
                      id="reason"
                      menuPaperProps={classes.menuPropsPaper}
                      menuPropsList={classes.menuPropsList}
                      classes={{ root: classes.generalReasonRoot, icon: classes.generalReasonIcon }}
                      items={rejectionReasonList || []}
                      disableUnderline
                      handleChange={(e: any) => handleCommonReasonChange(e)}
                    ></SelectBox>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );

  const getDetailsContent = () => (
    <Grid className={classes.contentContainer} container>
      <Grid container className="content">
        <Grid container className="itemContainer">
          <Grid className="itemColumn item1" item xs={4}>
            <Typography className={classes.title}>Product Name</Typography>
          </Grid>
          <Grid className="itemColumn item2" item xs={2} style={{ display: 'flex', flexDirection: 'column' }}>
            <Typography className={classes.title}>Rejected</Typography>
            <Typography className={classes.title} style={{ paddingTop: '4px' }}>
              Qty
            </Typography>
          </Grid>
          <Grid className="itemColumn item2" item xs={2}>
            <Typography className={classes.title}>Shipped</Typography>
          </Grid>
          <Grid className="itemColumn item2" item xs={2}>
            <Typography className={classes.title}>Ordered Qty</Typography>
          </Grid>
          <Grid className="itemColumn item3" item xs={2}>
            <Typography className={classes.title}>Unit Price</Typography>
          </Grid>
        </Grid>
      </Grid>
      {products?.length > 0 && getItems()}
      {bundleProduct?.length > 0 && getBundleProduct()}
      {products?.length === 0 && bundleProduct?.length === 0 && (
        <Grid style={{ padding: '12px' }}>
          <Typography style={{ fontSize: '14px', fontWeight: 500 }}>No Items to display</Typography>
        </Grid>
      )}
      <Grid className="paymentContainer" container direction="column">
        <Grid className="paymentItem" item>
          <Grid container>
            <Grid className="paymentItemColumn" item>
              <Typography>Payment Method</Typography>
            </Grid>
            <Grid className="paymentItemColumn right" item>
              <Typography>{metadata ? metadata.paymentType : ''}</Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid className="paymentItem" item>
          <Grid container>
            <Grid className="paymentItemColumn" item>
              <Typography>
                Grand Total<span className="vat">(Inclusive of VAT)</span>
              </Typography>
            </Grid>
            <Grid className="paymentItemColumn right" item>
              <Typography>
                {metadata
                  ? `${metadata.totalAmount ? metadata.totalAmount.toString() : ''} ${metadata.currency || ''}`
                  : ''}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        {metadata && metadata.paymentType === 'COD' ? (
          <Grid className="paymentItem" item>
            <Grid container>
              <Grid item>
                <Typography>Revised Total</Typography>
              </Grid>
              {revisedAmountAfterCalculation !== '' ? (
                <Grid className="paymentItemColumn right" item>
                  <Typography>{`${revisedAmountAfterCalculation} ${metadata && metadata.currency}`}</Typography>
                </Grid>
              ) : (
                <Grid className="paymentItemColumn right" item>
                  <Typography>{`${metadata.collectedAmount ? parseFloat(metadata.collectedAmount.toString()).toFixed(2) : '0.00'} ${metadata && metadata.currency}`}</Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
        ) : null}
      </Grid>
    </Grid>
  );

  const getDialogActions = () => (
    <Grid className={classes.buttons} container>
      <Button className="userBtn secondary" variant="contained" onClick={() => dialogActionHandler('cancel')}>
        Cancel
      </Button>
      <Button
        className="userBtn primary"
        variant="contained"
        type="submit"
        disabled={checkIfDisabled()}
        onClick={() => dialogActionHandler('save', products, bundleProducts, consignment)}
      >
        Save
      </Button>
    </Grid>
  );

  useEffect(() => {
    if (open && consignment != null) {
        fetchRejectionReasons(countryCode);
    }
  }, [consignment, countryCode, fetchRejectionReasons]);

  useEffect(() => {
    setBundleProducts(bundleRejectionDataTransform(bundleProduct) as any);
    setProducts(rejectionDataTransform(items) as any);
  }, [items, bundleProduct]);

  return (
    <CustomDialog
      open={open}
      TransitionComponent={Transition}
      PaperProps={dialogPaperProps}
      title={getTitleContent()}
      actions={getDialogActions()}
      content={getDetailsContent()}
      handleClose={handleDialogClose}
    ></CustomDialog>
  );
};

export default BasePartialRejectionDialog;
